import React, { type FC, useCallback, useEffect } from 'react';

import cn from 'classnames';

import { isMedia } from '@shared/lib/html';
import { Button } from '@shared/ui/button';
import { ContactItem } from '@shared/ui/contact-item';
import { DialPad, type DialPadSubmitPayload } from '@shared/ui/dial-pad';
import { Heading } from '@shared/ui/heading';
import IconClose from '@assets/img/icons/close-x.svg';
import IconMic from '@assets/img/icons/mic.svg';
import IconMicOff from '@assets/img/icons/mic-off.svg';
import IconUserMany from '@assets/img/icons/user-many.svg';
import IconUserPlus from '@assets/img/icons/user-plus.svg';
import IconVideo from '@assets/img/icons/video.svg';
import IconVideoOff from '@assets/img/icons/video-off.svg';

import { useCallsList, useDialPadSearch } from '../../hooks';
import { isParticipantSendingMedia } from '../../lib/misc';
import type { ConferenceUser } from '../../model/types';
import { ITransferPanel } from './types';

import styles from './styles.module.scss';

export const ConferencePanel: FC<ITransferPanel> = ({
	isOpened,
	onClose,
	defaultOpenKeys,
	onCreateConference,
	disabledCall,
}) => {
	const { activeConferenceUsers = [], pendingConferenceUsers = [] } = useCallsList();
	const handleResolvedDialPadSubmit = useCallback(
		({ aor, displayName }: DialPadSubmitPayload) => {
			onCreateConference({ aor, displayName });
			onClose();
		},
		[onClose, onCreateConference]
	);
	const {
		suggestions: dialPadSuggestions,
		onQueryChange: handleDialPadQueryChange,
		onSubmit: handleDialPadSearchSubmit,
	} = useDialPadSearch(handleResolvedDialPadSubmit);
	const participantsCount = activeConferenceUsers.length + pendingConferenceUsers.length;

	const freezeBodyScroll = (isFreeze: boolean) => {
		const { body } = document;

		if (isFreeze) {
			body.classList.add(styles.Freeze);
			const { scrollY } = window;
			body.style.position = 'fixed';
			body.style.top = `-${scrollY}px`;
		} else {
			body.classList.remove(styles.Freeze);
			const scrollY = body.style.top;
			body.style.position = '';
			body.style.top = '';
			window.scrollTo(0, Number.parseInt(scrollY || '0', 10) * -1);
		}
	};

	useEffect(() => {
		const onWindowResize = () => {
			if (!isMedia('sm')) {
				onClose();
			}
		};

		window.addEventListener('resize', onWindowResize);

		return () => window.removeEventListener('resize', onWindowResize);
	}, []);

	useEffect(() => {
		// for mobile only
		if (isMedia('sm')) freezeBodyScroll(isOpened);
	}, [isOpened]);

	const renderUser = (user: ConferenceUser) => {
		const hasAudio = isParticipantSendingMedia(user, 'audio');
		const hasVideo = isParticipantSendingMedia(user, 'video');

		return (
			<div key={user.id} className={styles.UserContainer}>
				<ContactItem
					// avatar={user.avatar}
					name={user.name || user.id}
				/>
				<div className={styles.UserMedia}>
					<span
						className={styles.IconMedia}
						dangerouslySetInnerHTML={{ __html: hasAudio ? IconMic : IconMicOff }}
					/>
					<span
						className={styles.IconMedia}
						dangerouslySetInnerHTML={{ __html: hasVideo ? IconVideo : IconVideoOff }}
					/>
				</div>
			</div>
		);
	};

	return (
		<div className={cn(styles.PanelWrapper, { [styles.active]: isOpened })}>
			<div className={styles.Headline}>
				<Heading noMargin className={styles.HeadlineText} size="h4">
					<span
						className={styles.HeadlineIcon}
						dangerouslySetInnerHTML={{ __html: IconUserMany }}
					/>
					Участники ({activeConferenceUsers.length > 0 ? participantsCount : 2})
				</Heading>
				<Button buttonType="icon" svg={IconClose} onClick={onClose} />
			</div>
			<div className={styles.PanelContent}>
				<div>
					<DialPad
						showInputButton
						classNames={styles.DialPadWrapper}
						defaultOpenKeys={defaultOpenKeys}
						disabledCall={disabledCall}
						suggestions={dialPadSuggestions}
						svgButton={IconUserPlus}
						textButton="Добавить участника"
						onClick={handleDialPadSearchSubmit}
						onQueryChange={handleDialPadQueryChange}
					/>

					{(activeConferenceUsers.length > 0 || pendingConferenceUsers.length > 0) && (
						<div className={styles.UsersWrapper}>
							{activeConferenceUsers.map((user) => renderUser(user))}

							{pendingConferenceUsers.length > 0 && (
								<>
									<span className={styles.PendingText}>Ожидание</span>
									{pendingConferenceUsers.map((user) => renderUser(user))}
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
